import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(Cookies.get('sankalvax_token'));

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = Cookies.get('sankalvax_token');
      const storedUser = Cookies.get('sankalvax_user');
      
      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          
          // Verify token is still valid
          await authAPI.verifyToken(storedToken);
        } catch (error) {
          console.error('Token verification failed:', error);
          await logout();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      // Don't set isLoading here - Login component has its own loading state
      // Setting it here causes AppRoutes to show loading screen and unmount Login
      const response = await authAPI.login(credentials);

      console.log('🔐 [DEBUG] Login response:', response);

      // Check if MFA is required
      if (response.mfa_required) {
        console.log('🔐 [AUTH DEBUG] MFA required detected in response');
        return { success: false, mfa_required: true, message: response.message };
      }

      if (response.success) {
        // Extract token from session object and user data
        const newToken = response.session.token;
        const userData = response.user;

        setToken(newToken);
        setUser(userData);

        Cookies.set('sankalvax_token', newToken, { expires: 7 });
        Cookies.set('sankalvax_user', JSON.stringify(userData), { expires: 7 });

        toast.success('Login successful!');
        return { success: true };
      } else {
        toast.error(response.message || 'Login failed');
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);

      // Handle different error response formats
      let message = 'Login failed. Please try again.';
      let mfa_required = false;
      let errorCode = null;

      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.mfa_required) {
          // MFA required response
          mfa_required = true;
          message = error.response.data.message || 'MFA verification required';
        } else if (error.response.data.message) {
          message = error.response.data.message;
        } else if (error.response.data.error?.message) {
          message = error.response.data.error.message;
          errorCode = error.response.data.error?.error;
        } else if (error.response.data.detail?.message) {
          message = error.response.data.detail.message;
          errorCode = error.response.data.detail?.error;
        } else if (error.response.data.detail) {
          message = 'Invalid request format';
        }
      }

      if (!mfa_required) {
        toast.error(message);
      }
      return { success: false, mfa_required, message, error: errorCode };
    }
    // Don't set isLoading in finally - Login component manages its own loading state
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      const response = await authAPI.register(userData);
      
      if (response.success) {
        toast.success('Registration successful! Please log in.');
        return { success: true };
      } else {
        toast.error(response.message || 'Registration failed');
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      // Backend returns error in detail.message for HTTPException
      const message = error.response?.data?.detail?.message ||
                      error.response?.data?.message ||
                      'Registration failed. Please try again.';
      toast.error(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await authAPI.logout(token);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      Cookies.remove('sankalvax_token');
      Cookies.remove('sankalvax_user');
      toast.success('Logged out successfully');
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    Cookies.set('sankalvax_user', JSON.stringify(updatedUser), { expires: 7 });
  };

  const isAuthenticated = () => {
    return !!(token && user);
  };

  const value = {
    user,
    token,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: isAuthenticated(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};